/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Builds the dashboard product catalogue from the Stripe customer record and
 * the account's orders, and reports when it is ready.
 *
 * This ran inline in App.tsx, where it could not be exercised without standing
 * up the whole router. It decides whether My Products shows a placeholder or
 * the customer's licences, so it is worth being able to test on its own.
 *
 * The readiness flag means "the catalogue has been built at least once". It is
 * never set back to false here. A rebuild runs whenever orders or profile data
 * changes -- including the refresh that opening the licence key dialog
 * triggers -- and clearing the flag mid-use turned the dashboard back into a
 * placeholder, unmounted the product table and destroyed the open dialog with
 * it. Sign-out resets the flag; nothing else needs to.
 */

import {
  adaptActiveSubscription,
  adaptOneTimePurchases,
  adaptProducts,
  adaptSubscriptionProducts,
} from "@/adapters/productsAdapter";
import {
  setActiveSubscriptions,
  setDashboardCatalogReady,
  setInvoices,
  setProducts,
} from "@/redux/slices/userSlice";
import { logger } from "@/lib/logger";
import type { Order } from "@/types";
import type { Dispatch, UnknownAction } from "@reduxjs/toolkit";

/** Minimal shape needed here: the adapters own the detail. */
type StripeCustomerLike = Parameters<typeof adaptActiveSubscription>[0];
/** The account's invoices, as the store holds them and the adapters read them. */
type OrdersLike = Order[];

export interface BuildDashboardCatalogArgs {
  stripeCustomer: StripeCustomerLike | null | undefined;
  orders: OrdersLike | null | undefined;
  /** True while either the profile or the orders request is still in flight. */
  isLoading: boolean;
  /**
   * VS-2 (S712): true while either query is SKIPPED and has therefore never
   * run.
   *
   * A skipped RTK Query reports `isLoading: false` with no data, which is
   * indistinguishable here from a request that finished and found nothing --
   * and "finished and found nothing" is a final answer that marks the catalogue
   * ready. Before sign-in both queries are skipped, so the catalogue was marked
   * ready and EMPTY; when sign-in then started the real fetch, `isLoading`
   * became true and this function returned early WITHOUT clearing the flag.
   * License Details, which waits on the flag, saw a ready empty catalogue and
   * showed "License Not Found" until the page was refreshed.
   *
   * `isUninitialized` is the one signal that tells the two apart.
   */
  isUninitialized?: boolean;
  /**
   * VS-2 (S712): whether there is a session at all.
   *
   * With no session there is nothing to build and nothing to wait for, and
   * marking the catalogue ready is correct -- it is what stops a GUEST seeing a
   * placeholder that never resolves. It is passed explicitly rather than
   * inferred from the absence of data, because those are the two states this
   * item exists to tell apart.
   */
  hasSession?: boolean;
  dispatch: Dispatch<UnknownAction>;
}

/**
 * Builds the catalogue and writes it to the store.
 *
 * Returns true when the catalogue was marked ready, false when the call was a
 * no-op because the source data has not arrived yet. The return value exists
 * for tests and callers that want to assert the outcome; the store is the
 * real output.
 */
export async function buildDashboardCatalog({
  stripeCustomer,
  orders,
  isLoading,
  isUninitialized = false,
  hasSession = true,
  dispatch,
}: BuildDashboardCatalogArgs): Promise<boolean> {
  // Still waiting on the source data. This is the only genuine waiting state,
  // and the only case where the catalogue is left not-ready.
  if (isLoading) {
    logger.debug("Still loading profile or orders...");
    return false;
  }

  // VS-2 (S712): a query that has never run is not a query that came back
  // empty. Building here is what marked the catalogue ready and empty before
  // sign-in, so that the real fetch which followed had nothing to correct.
  //
  // With no session this is the guest case and the catalogue IS ready: there
  // is nothing to fetch and nothing to wait for, and leaving it not-ready puts
  // a placeholder on screen for ever.
  if (isUninitialized && hasSession) {
    logger.debug("Profile or orders not requested yet -- nothing to build");
    return false;
  }

  // Both requests have finished. If either came back empty there is nothing to
  // build, and that is a final answer rather than a waiting state -- an
  // account with no Stripe customer record owns nothing here. Marking the
  // catalogue ready is what stops the dashboard showing a placeholder that
  // never resolves.
  if (!stripeCustomer) {
    logger.debug("No stripe customer data available");
    dispatch(setDashboardCatalogReady(true));
    return true;
  }

  if (!orders) {
    logger.debug("No orders data available");
    dispatch(setDashboardCatalogReady(true));
    return true;
  }

  try {
    dispatch(setInvoices(orders));

    // Each adaptation is guarded on its own so that one failing source does
    // not empty the whole catalogue. A customer whose subscription rows cannot
    // be read should still see everything else the account holds.
    let activeSubscriptions: Awaited<
      ReturnType<typeof adaptActiveSubscription>
    > = [];
    try {
      activeSubscriptions = await adaptActiveSubscription(
        stripeCustomer,
        orders ?? [],
      );
    } catch (error) {
      logger.error("Error adapting active subscriptions:", error);
    }
    dispatch(setActiveSubscriptions(activeSubscriptions));

    let subscriptionProductsData: Awaited<
      ReturnType<typeof adaptSubscriptionProducts>
    > = [];
    try {
      subscriptionProductsData = await adaptSubscriptionProducts(
        activeSubscriptions,
        orders ?? [],
      );
    } catch (error) {
      logger.error("Error adapting subscription products:", error);
    }

    // The second source of rows. Nothing in the subscription records above
    // mentions a one-time purchase -- Document Translator word credits are the
    // live example -- so without this the customer pays and My Products shows
    // nothing. Guarded on its own for the same reason as the block above.
    let oneTimePurchases: Awaited<ReturnType<typeof adaptOneTimePurchases>> =
      [];
    try {
      oneTimePurchases = await adaptOneTimePurchases(orders);
    } catch (error) {
      logger.error("Error adapting one-time purchases:", error);
    }

    try {
      const products = await adaptProducts(
        subscriptionProductsData,
        oneTimePurchases,
      );
      logger.debug("Total products dispatched:", products.length);
      dispatch(setProducts(products));
    } catch (error) {
      logger.error("Error adapting products:", error);
      // IM-1 (S607): keep the previous catalogue rather than emptying it.
      //
      // This used to dispatch setProducts([]), reasoning that "an empty
      // catalogue is still an answer" and that "leaving the previous one in
      // place would show products the account may no longer own". That holds
      // for a page load, where there is no previous catalogue to keep. It is
      // wrong for a refresh: opening the Licence Keys dialog triggers one, so
      // a single failed adaptation deleted the row the dialog was reading and
      // took the dialog with it (PYH-10).
      //
      // Dispatching nothing keeps both cases right. On a first load the
      // catalogue is already empty, so the customer sees exactly what the old
      // dispatch showed them; on a refresh they keep the rows they had. The
      // failure is still logged, the catalogue is still marked ready below,
      // and My Products keeps the retry control that refetches both sources.
    }
  } catch (error) {
    logger.error("Error building dashboard catalog:", error);
  } finally {
    dispatch(setDashboardCatalogReady(true));
  }

  return true;
}
